import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { HttpHelpersService, LoadingService } from '@coin/shared/core/data-access';

import { environment } from '@coin/shared/core/util-environments';
import { ToastrService } from 'ngx-toastr';
import { from, Observable } from 'rxjs';
import { finalize, map, retryWhen, tap } from 'rxjs/operators';
import { checkResult, encodeSeasonType, pollingStrategy, RuleEngineHelper } from '@coin/shared/core/util-helpers';
import {
  Employee,
  GenericSimulationResponse,
  MassExportTransactionStatus,
  PaginatedResult,
  RuleSetV2,
  TransactionStatus,
  TransactionStatusMetadata
} from '@coin/shared/core/util-models';
import { SeasonTypeEnum } from '@coin/shared/core/util-enums';
import { InputTableType } from '@coin/admin/core/util';
import { FileUploadService } from '../../org-planning/services/upload-file.service';
import { ImporterName } from '../../interfaces/enums/importer-name.enum';
import { SeasonSimulation } from '../models/season-simulation.model';
import { PopulationTypeEnum } from '../enums/population-type.enum';

@Injectable({
  providedIn: 'root'
})
export class SeasonSimulationService {
  private httpClient = inject(HttpClient);
  private httpHelpersService = inject(HttpHelpersService);
  private loadingService = inject(LoadingService);
  private toast = inject(ToastrService);
  private fileUploadService = inject(FileUploadService);
  private httpHelper = inject(HttpHelpersService);

  public getSimulations(seasonType: SeasonTypeEnum, seasonId: string): Observable<SeasonSimulation[]> {
    return this.httpClient
      .get<
        SeasonSimulation[]
      >(`${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType)}/seasons/${seasonId}/simulations`)
      .pipe(this.httpHelpersService.handleError('Cannot get simulations'));
  }

  public getSimulationById(seasonType: SeasonTypeEnum, simId: string): Observable<SeasonSimulation> {
    return this.httpClient
      .get<SeasonSimulation>(
        `${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType)}/simulations/${simId}`
      )
      .pipe(
        map(checkResult()),
        retryWhen(pollingStrategy()),
        this.httpHelpersService.handleError('Cannot get simulation')
      );
  }

  public createSimulation(
    seasonType: SeasonTypeEnum,
    body: SeasonSimulation
  ): Observable<TransactionStatus<TransactionStatusMetadata>> {
    this.loadingService.present();

    return this.httpClient
      .post<
        TransactionStatus<TransactionStatusMetadata>
      >(`${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType)}/simulations`, body)
      .pipe(
        tap(() => {
          this.toast.success(`Simulation "${body.title}" created.`);
        }),
        this.httpHelpersService.handleError('Cannot create simulation'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  public updateSimulation(seasonType: SeasonTypeEnum, simulation: SeasonSimulation): Observable<SeasonSimulation> {
    this.loadingService.present();
    return this.httpClient
      .patch<SeasonSimulation>(
        `${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType)}/simulations/${simulation.id}`,
        simulation
      )
      .pipe(
        this.httpHelpersService.handleError('Cannot update simulation'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  public updateSimulationMembers(
    simulation: SeasonSimulation,
    seasonType: SeasonTypeEnum
  ): Observable<SeasonSimulation> {
    this.loadingService.present();
    return this.httpClient
      .patch<SeasonSimulation>(
        `${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType)}/seasons/${simulation.seasonId}/simulations/${simulation.id}/update-members`,
        {}
      )
      .pipe(
        this.httpHelpersService.handleError('Cannot update simulation members'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  public prepublish(simId: string, seasonType: SeasonTypeEnum): Observable<TransactionStatus> {
    this.loadingService.present();
    return this.httpClient
      .post<TransactionStatus>(
        `${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType)}/simulations/${simId}/pre-publish`,
        {}
      )
      .pipe(
        this.httpHelpersService.handleError('Cannot pre publish simulation'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  // ids have priority, if set, query will be ignored
  public massPrepublishPositions(
    simId: string,
    seasonType: SeasonTypeEnum,
    ids: string[],
    filterText: string
  ): Observable<TransactionStatus> {
    this.loadingService.present();
    return this.httpClient
      .post<TransactionStatus>(
        `${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType)}/simulations/${simId}/pre-publish?${filterText}`,
        {
          seasonType,
          ids
        }
      )
      .pipe(
        this.httpHelpersService.handleError('Cannot pre publish positions'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  public massUnpublishPositions(
    simId: string,
    seasonType: SeasonTypeEnum,
    ids: string[],
    filterText: string
  ): Observable<TransactionStatus> {
    this.loadingService.present();
    return this.httpClient
      .post<TransactionStatus>(
        `${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType)}/simulations/${simId}/un-publish?${filterText}`,
        {
          seasonType,
          ids
        }
      )
      .pipe(
        this.httpHelpersService.handleError('Cannot unpublish positions'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  public massPublishPositions(
    simId: string,
    seasonType: SeasonTypeEnum,
    ids: string[],
    filterText: string
  ): Observable<TransactionStatus> {
    this.loadingService.present();
    return this.httpClient
      .post<TransactionStatus>(
        `${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType)}/simulations/${simId}/publish?${filterText}`,
        {
          seasonType,
          ids
        }
      )
      .pipe(
        this.httpHelpersService.handleError('Cannot publish positions'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  public unpublish(simId: string, seasonType: SeasonTypeEnum): Observable<TransactionStatus> {
    this.loadingService.present();
    return this.httpClient
      .post<TransactionStatus>(
        `${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType)}/simulations/${simId}/un-pre-publish`,
        {}
      )
      .pipe(
        this.httpHelpersService.handleError('Cannot unpublish simulation'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  public publish(simId: string, seasonType: SeasonTypeEnum): Observable<TransactionStatus> {
    this.loadingService.present();
    return this.httpClient
      .post<TransactionStatus>(
        `${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType)}/simulations/${simId}/publish`,
        {}
      )
      .pipe(
        this.httpHelpersService.handleError('Cannot publish simulation'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  public calculateSimulation({
    seasonType,
    seasonId,
    simId
  }: {
    seasonType: SeasonTypeEnum;
    seasonId: string;
    simId: string;
  }): Observable<TransactionStatus> {
    this.loadingService.present();

    return this.httpClient
      .post<TransactionStatus>(
        `${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType)}/seasons/${seasonId}/simulations/calculate`,
        {
          simulationIds: [simId]
        }
      )
      .pipe(finalize(() => this.loadingService.dismiss()));
  }

  public getSimulatedPopulation(
    seasonType: SeasonTypeEnum,
    body: RuleSetV2,
    populationType: PopulationTypeEnum,
    take: number,
    skip: number,
    seasonId: string,
    simulationId: string,
    settingsId: string,
    subSettingsId?: string
  ): Observable<GenericSimulationResponse> {
    const page = !skip ? 1 : Math.floor(skip / take) + 1;

    let url = `/admin/api/v1/${encodeSeasonType(seasonType)}/seasons/${seasonId}/simulations/${simulationId}/`;

    switch (populationType) {
      case PopulationTypeEnum.MERIT_SETTINGS:
        url += `merit-settings/${settingsId}/simulate-population`;
        break;
      case PopulationTypeEnum.EQUITY_SETTINGS:
        url += `equity-settings/${settingsId}/simulate-population`;
        break;
      case PopulationTypeEnum.EQUITY_SUB_SETTINGS:
        url += `equity-settings/${settingsId}/equity-sub-settings/${subSettingsId}/simulate-population`;
        break;
    }

    return this.httpClient
      .post<{
        employeesAdded: PaginatedResult<Employee>;
        employeesNotAdded: PaginatedResult<Employee>;
      }>(`${environment.api.baseUrl}${url}?page=${page}&size=${take}`, RuleEngineHelper.ruleSetV2ToDto(body))
      .pipe(
        map(result => ({ entitiesAdded: result?.employeesAdded, entitiesNotAdded: result?.employeesNotAdded })),
        this.httpHelpersService.handleError('Cannot simulate population')
      );
  }

  public recalculatePopulation(
    seasonType: SeasonTypeEnum,
    simId: string,
    type: InputTableType,
    parentSettingId?: string
  ): Observable<TransactionStatus> {
    this.loadingService.present();

    let params = new HttpParams();
    let subUrl = '';

    params = params.set('settingsType', type);

    if (parentSettingId) {
      subUrl = `/${parentSettingId}`;
    }

    return this.httpClient
      .post<TransactionStatus>(
        `${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType)}/simulations/${simId}/population-recalculation${subUrl}`,
        {},
        { params }
      )
      .pipe(
        tap(() => this.toast.success('Population recalculation started.')),
        finalize(() => this.loadingService.dismiss())
      );
  }

  public deleteSimulationSettings(
    seasonId: string,
    simulationId: string,
    settingsId: string,
    settingsType: InputTableType,
    seasonType: SeasonTypeEnum
  ): Observable<void> {
    this.loadingService.present();
    return this.httpClient
      .delete<void>(
        `${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType)}/seasons/${seasonId}/simulations/${simulationId}/settings/${settingsId}?settingsType=${settingsType}`
      )
      .pipe(
        tap(() => this.toast.success('Deleted.')),
        this.httpHelpersService.handleError('Cannot delete settings.'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  public exportEmployeeTags(simulationId: string): Observable<TransactionStatus<MassExportTransactionStatus>> {
    return this.httpClient
      .get<
        TransactionStatus<MassExportTransactionStatus>
      >(`${environment.api.baseUrl}/admin/api/v2/master/seasons/merit/simulations/members/${simulationId}/tags/export`)
      .pipe(this.httpHelper.handleError('Cannot export tags'), this.loadingService.withLoadingScreen);
  }

  public exportMidYearIncrease(simulationId: string): Observable<TransactionStatus<MassExportTransactionStatus>> {
    return this.httpClient
      .get<
        TransactionStatus<MassExportTransactionStatus>
      >(`${environment.api.baseUrl}/admin/api/v2/master/seasons/merit/simulations/members/${simulationId}/mid-year-increase/export`)
      .pipe(this.httpHelper.handleError('Cannot export mid-year-increase'), this.loadingService.withLoadingScreen);
  }

  public uploadEmployeeTags(simulationId: string, file: File): Observable<{ transactionId: string }> {
    return from(
      this.fileUploadService.uploadSingleFile(
        file,
        ImporterName.MeritSimulationMemberTags,
        { simulationid: simulationId },
        true
      )
    ).pipe(map(id => ({ transactionId: id })));
  }

  public uploadMidYearIncrease(simulationId: string, file: File): Observable<{ transactionId: string }> {
    return from(
      this.fileUploadService.uploadSingleFile(file, ImporterName.MidYearIncreases, { simulationid: simulationId }, true)
    ).pipe(map(id => ({ transactionId: id })));
  }
}
